/* Source: /home/sidsolaiyappan/skooch_ai/chat/chat_actions.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { validateCreateTaskAction, resolveDependsOnTitles, buildLocalAnonTask, validateCreateFixedEventAction, computeFixedEventConflictMsg, buildStructuredRule, addRuleReceipt, findTaskByFuzzyTitle, findFixedBlockByFuzzyTitle, applyRescheduleUpdates, findTrashTombstoneByTitle, trashTombstoneTitle, _normalizeGapSide, _gapRuleLabel, _setGapReceipt, _rebalanceReceipt } from "../shared/ai_action_helpers.js";
import { normalizeAiCategory, clampAiPriority, resolveAiTaskDeadline, nextAiLocalId, minutesFromClockRange, aiRuleStorageType, aiRuleLabel, _normForMatch, stripTrailingDot, groupActionSummaries, formatDurationMins, _aiDateToIso, deleteReceipt } from "../shared/ai_message_helpers.js";
import { pbResolveWritePriority } from "../shared/chip_category.js";
import { validateAiActions, translateReplanDelta } from "../shared/action_validator.js";
import { snapshotPlacements, describeMoveOutcome, summarizeOptimizeOutcome, describeFixedEventWhen, buildIntendedFromActions, reconcileActionOutcomes, buildOutcomeMessage, partitionCreationSummaries, formatDayDate } from "../shared/action_outcomes.js";
import { saveChatToStorage } from "./chat_session.js";
import { getAccessToken } from "../shared/auth_token.js";
import { createFixedEventRemote, updateFixedEventRemote, deleteFixedEventRemote, deleteAllFixedEventsRemote } from "../shared/fixed_event_crud.js";
import { _addMinutesToTime, _minutesBetween, _hhmm_to_mins } from "../shared/scheduler_math.js";
import { computeMovedOccurrenceInstants } from "../shared/event_edit_transforms.js";
import { refreshFixedBlocksAfterSync, pushGcalAfterLocalChange, _gcalServerlessInvoke } from "../shared/gcal_connect.js";
import { buildFixedEventBlocks } from "../shared/fixed_event_helpers.js";
import { addStructuredGlobalRuleRemote } from "../shared/global_rules_crud.js";
import { upsertScheduleBlocks } from "../shared/schedule_block_crud.js";
import { clientErrorBody, schedulerErrorText, schedulerRequestFailed } from "../shared/scheduler_request_helpers.js";
import { buildPlacementExplanation } from "../shared/placement_explanation.js";
import { get_supabase } from "../shared/supabase_config.js";
import { createTaskRemote, deleteTaskRemote, updateTaskFullRemote, updateTaskStatusOnlyRemote } from "../shared/task_crud_remote.js";
import { computeAcceptAllPlacements, computeAcceptedStatusTasks } from "../shared/task_handlers.js";
import { runOptimize } from "../scheduler/scheduler_optimize.js";
import { logInteractionEvent } from "../shared/event_capture.js";
import { getStoreTasks, getStoreSchedule, getStoreFixedBlocks, getStoreGlobalRules, getStoreOverflow, getStoreUserId, getStoreIsAnonymous, getStoreAiPendingPlan, getStoreAiActiveChatId, getStoreAiChatMessages, setStoreTasks, setStoreSchedule, setStoreFixedBlocks, setStoreGlobalRules, setStoreSchedulerTasks, setStoreOverflow, setStoreSchDirty, setStoreSchOptimized, setStoreAiPendingPlan, setStoreAiTimeSuggestions, setStoreAiChatBusy } from "../shared/store.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
async function executeAiCreateTaskResult(action, sourcePrompt = "") {
  let _tasks = getStoreTasks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _v = validateCreateTaskAction(action, sourcePrompt, normalizeAiCategory, clampAiPriority, resolveAiTaskDeadline);
  if (!_v["ok"]) {
    return {"ok": false, "msg": _v["msg"], "id": "", "task": null};
  }
  let createdTaskId = "";
  let _newTaskList = _tasks;
  let _resultTask = null;
  let _priority = pbResolveWritePriority(_v["category"], _v["priority"]);
  _v["priority"] = _priority;
  let _resolvedDeps = resolveDependsOnTitles((("dependsOn" in _v) ? _v["dependsOn"] : []), getStoreTasks());
  _v["dependsOn"] = _resolvedDeps;
  let _seq = {"before_event_id": (("beforeEventId" in _v) ? _v["beforeEventId"] : ""), "depends_on": _resolvedDeps};
  if (_uid) {
    let accessToken = await getAccessToken();
    let savedTask = await createTaskRemote(_uid, _isAnon, accessToken, _v["title"], _v["category"], _v["durationMins"], _priority, _v["deadline"], {}, _seq);
    if (schedulerRequestFailed(savedTask)) {
      return {"ok": false, "msg": schedulerErrorText("I couldn't create that task", savedTask), "id": "", "task": null};
    }
    createdTaskId = savedTask.id;
    if (_v["prefTime"]) {
      savedTask["preferred_time"] = _v["prefTime"];
    }
    if (_v["prefDate"]) {
      savedTask["preferred_date"] = _v["prefDate"];
    }
    if (_seq["before_event_id"]) {
      savedTask["before_event_id"] = _seq["before_event_id"];
    }
    if ((_seq["depends_on"] && (_seq["depends_on"].length > 0))) {
      savedTask["depends_on"] = _seq["depends_on"];
    }
    _newTaskList = getStoreTasks().concat([savedTask]);
    _resultTask = savedTask;
  } else {
    createdTaskId = nextAiLocalId("task-local-");
    _resultTask = buildLocalAnonTask(createdTaskId, _v);
    _newTaskList = getStoreTasks().concat([_resultTask]);
  }
  setStoreTasks(_newTaskList);
  window.__skoochSyncSchTasksFromPending(_newTaskList);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  let _durLbl = formatDurationMins(_v["durationMins"]);
  try {
    logInteractionEvent("chat_action_executed", {"entity_id": createdTaskId, "category": (_v["category"] ? _v["category"] : null), "payload": {"action_type": "create_task", "action": action, "source_prompt": sourcePrompt}});
  } catch (__jac_e) {
    if (true) {
      let _ele = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return {"ok": true, "msg": ((("Added task: " + _v["title"]) + (_durLbl ? ((" (" + _durLbl) + ")") : "")) + "."), "id": createdTaskId, "task": _resultTask};
}
async function executeAiCreateTask(action, sourcePrompt = "") {
  let res = await executeAiCreateTaskResult(action, sourcePrompt);
  if (!res.ok) {
    return res.msg;
  }
  return res.msg;
}
async function executeAiCreateFixedEvent(action) {
  let _schedule = getStoreSchedule();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _fixedBlocks = getStoreFixedBlocks();
  let _v = validateCreateFixedEventAction(action, normalizeAiCategory, minutesFromClockRange);
  if (!_v["ok"]) {
    return _v["error"];
  }
  let title = _v["title"];
  let isAllDay = _v["isAllDay"];
  let startTime = _v["startTime"];
  let endTime = _v["endTime"];
  let days = _v["weekdays"];
  let isRecurring = _v["isRecurring"];
  let scheduledDate = _v["scheduledDate"];
  let durationMins = _v["durationMins"];
  let category = _v["category"];
  let _conflictMsg = (!isAllDay ? computeFixedEventConflictMsg(_schedule, startTime, endTime, days, isRecurring, scheduledDate) : "");
  if (_uid) {
    let accessToken = await getAccessToken();
    let _fer = await createFixedEventRemote(_uid, _isAnon, accessToken, title, category, "", startTime, endTime, durationMins, days, isRecurring, scheduledDate, isAllDay);
    if (!_fer["ok"]) {
      return (_fer["errorMsg"] ? _fer["errorMsg"] : "I couldn't save that event right now.");
    }
    setStoreFixedBlocks(_fixedBlocks.concat(_fer["blocks"]));
  } else {
    if (!isRecurring) {
      return "I need a signed-in session to create a dated fixed event right now.";
    }
    setStoreFixedBlocks(_fixedBlocks.concat(buildFixedEventBlocks(nextAiLocalId("fe-local-"), title, category, startTime, endTime, durationMins, days, isAllDay)));
  }
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  let _whenTxt = describeFixedEventWhen(days, isRecurring, scheduledDate);
  let _baseMsg = "";
  if (isAllDay) {
    _baseMsg = ((("Added all-day event: " + title) + (_whenTxt ? (", " + _whenTxt) : "")) + ".");
  } else {
    _baseMsg = ((((((((("Added " + (isRecurring ? "recurring " : "")) + "event: ") + title) + " (") + startTime) + "-") + endTime) + (_whenTxt ? (", " + _whenTxt) : "")) + ").");
  }
  if (_conflictMsg) {
    return ((_baseMsg + " ") + _conflictMsg);
  }
  return _baseMsg;
}
async function executeAiAddRule(action) {
  let _globalRules = getStoreGlobalRules();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let rule = (_jac.poly.and_(action, () => action.rule) ? action.rule : {});
  let _r = buildStructuredRule(rule, aiRuleStorageType, normalizeAiCategory, aiRuleLabel, nextAiLocalId);
  if (!_r["ok"]) {
    return _r["error"];
  }
  let storedRule = _r["rule"];
  setStoreGlobalRules(_globalRules.concat([storedRule]));
  if (_uid) {
    let accessToken = await getAccessToken();
    let _ok = await addStructuredGlobalRuleRemote(_uid, _isAnon, accessToken, storedRule["id"], storedRule);
    if (!_ok) {
      setStoreGlobalRules(_globalRules.filter(r => {
        return (r.id !== storedRule["id"]);
      }));
      return "I couldn't save that rule.";
    }
  }
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  return addRuleReceipt(rule, storedRule);
}
async function executeAiSetGap(action) {
  let _globalRules = getStoreGlobalRules();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _aRaw = (_jac.poly.and_(action, () => action.a_match) ? action.a_match : {});
  let _bRaw = (_jac.poly.and_(action, () => action.b_match) ? action.b_match : {});
  let _gapMins = (_jac.poly.and_(action, () => (action.min_gap_mins !== undefined)) ? parseInt(String(action.min_gap_mins)) : 0);
  if ((_gapMins !== _gapMins)) {
    _gapMins = 0;
  }
  if ((_gapMins <= 0)) {
    return "I couldn't set that gap \u2014 tell me how many minutes to keep between them (e.g. 'leave an hour between my gym and my coffee').";
  }
  let _aSide = _normalizeGapSide(_aRaw, normalizeAiCategory);
  let _bSide = _normalizeGapSide(_bRaw, normalizeAiCategory);
  if ((!_jac.builtin.bool(_aSide) || !_jac.builtin.bool(_bSide))) {
    return "I couldn't tell which two things to keep apart \u2014 try 'leave an hour between my gym and my coffee' or 'space my workouts out'.";
  }
  let _ruleId = nextAiLocalId("r-gap-");
  let _label = _gapRuleLabel(_aSide, _bSide, _gapMins);
  let storedRule = {"id": _ruleId, "type": "min_gap", "a_match": _aSide, "b_match": _bSide, "min_gap_mins": _gapMins, "label": _label};
  setStoreGlobalRules(_globalRules.concat([storedRule]));
  if (_uid) {
    let accessToken = await getAccessToken();
    let _ok = await addStructuredGlobalRuleRemote(_uid, _isAnon, accessToken, _ruleId, storedRule);
    if (!_ok) {
      setStoreGlobalRules(_globalRules.filter(r => {
        return (r.id !== _ruleId);
      }));
      return "I couldn't save that gap.";
    }
  }
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  return _setGapReceipt(_aSide, _bSide, _gapMins);
}
async function executeAiRepeatTask(action) {
  let _title = (_jac.poly.and_(action, () => action.title) ? String(action.title).trim() : "");
  if (!_title) {
    return "I couldn't set up that habit \u2014 tell me what it is (e.g. 'gym 3x a week, flexible').";
  }
  let _n = (_jac.poly.and_(_jac.poly.and_(action, () => (action.times_per_week !== undefined)), () => (action.times_per_week !== null)) ? parseInt(String(action.times_per_week)) : 0);
  if ((_n !== _n)) {
    _n = 0;
  }
  if ((_n < 1)) {
    return (("I couldn't tell how often '" + _title) + "' should happen \u2014 say how many times a week (e.g. 'gym 3x a week').");
  }
  if ((_n > 7)) {
    _n = 7;
  }
  let _weekdays = [];
  let _rawWds = (_jac.poly.and_(action, () => action.weekdays) ? action.weekdays : []);
  if ((_rawWds && (_rawWds.length > 0))) {
    for (const _wd of _rawWds) {
      let _wdi = parseInt(String(_wd));
      if (((((_wdi === _wdi) && (_wdi >= 1)) && (_wdi <= 7)) && !_weekdays.includes(_wdi))) {
        _weekdays.push(_wdi);
      }
    }
  }
  let _dur = (_jac.poly.and_(_jac.poly.and_(action, () => (action.duration_mins !== undefined)), () => (action.duration_mins !== null)) ? parseInt(String(action.duration_mins)) : 0);
  if (((_dur !== _dur) || (_dur < 1))) {
    _dur = 45;
  }
  let _pattern = {"type": "weekly", "times_per_week": _n, "weekdays": _weekdays, "until": ""};
  let _createAction = {"type": "create_task", "title": _title, "category": (_jac.poly.and_(action, () => action.category) ? action.category : "other"), "duration_mins": _dur, "priority": 5};
  let _res = await executeAiCreateTaskResult(_createAction, "");
  if (!_res["ok"]) {
    return _res["msg"];
  }
  let _taskId = _res["id"];
  let _tasksNow = getStoreTasks();
  let _patched = _tasksNow.map(t => {
    if ((t && (t.id === _taskId))) {
      let _copy = {};
      for (const _k of _jac.poly.call(Object, "keys", t)) {
        _copy[_k] = _jac.poly.getitem(t, _k);
      }
      _copy["recurrence_pattern"] = _pattern;
      return _copy;
    }
    return t;
  });
  setStoreTasks(_patched);
  window.__skoochSyncSchTasksFromPending(_patched);
  let _uid = getStoreUserId();
  if ((_uid && _taskId)) {
    try {
      let _sb = get_supabase();
      await _jac.poly.call(_sb.from("tasks"), "update", {"recurrence_pattern": _pattern}).eq("id", _taskId).eq("user_id", _uid);
    } catch (__jac_e) {
      if (true) {
        let _exrp = __jac_e;
        console.log("repeat_task recurrence_pattern persist (best-effort) failed:", _exrp);
      } else {
        throw __jac_e;
      }
    }
  }
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  let _whenTxt = "";
  if (_jac.builtin.bool(_jac.poly.and_(_weekdays, () => (_weekdays.length > 0)))) {
    let _dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    let _dayLabels = [];
    for (const _dwd of _weekdays) {
      if (((_dwd >= 1) && (_dwd <= 7))) {
        _dayLabels.push(_dayNames.at((_dwd - 1)));
      }
    }
    if ((_dayLabels.length > 0)) {
      _whenTxt = (" on " + _jac.poly.call(_dayLabels, "join", ", "));
    }
  }
  return (((((("Set up " + _title) + " ~") + String(_n)) + "x/week") + _whenTxt) + " \u2014 I'll place it fresh each week.");
}
async function executeAiRebalanceWeek(action) {
  let _FORCE_SPREAD = 40;
  let _targetDay = 0;
  if (_jac.builtin.bool(_jac.poly.and_(_jac.poly.and_(action, () => (action.target_day !== undefined)), () => (action.target_day !== null)))) {
    let _td = parseInt(String(action.target_day));
    if ((((_td === _td) && (_td >= 1)) && (_td <= 7))) {
      _targetDay = _td;
    }
  }
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  await runOptimize(null, null, null, null, _FORCE_SPREAD);
  let _receipt = _rebalanceReceipt(_targetDay);
  let _isAnonNow = (window.__skoochIsAnon !== false);
  let _profileDone = (window.__skoochProfileComplete === true);
  if ((!_isAnonNow && _profileDone)) {
    return _receipt;
  }
  if (_isAnonNow) {
    return (_receipt + " (This is a preview \u2014 sign up and finish the quick setup for fully optimized scheduling.)");
  }
  return (_receipt + " (This is a preview \u2014 finish the quick survey for fully optimized scheduling.)");
}
async function executeAiExplainSchedule(action) {
  let _schedule = getStoreSchedule();
  let _overflow = getStoreOverflow();
  let targetId = (_jac.poly.and_(action, () => action.task_id) ? String(action.task_id).trim() : "");
  let targetTitle = (_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).toLowerCase().trim() : "");
  let targetBlock = {};
  let _matched = false;
  for (const blk of _schedule) {
    let blkTitle = ((blk && blk.task_title) ? String(blk.task_title).toLowerCase().trim() : "");
    if (((targetId && (blk.task_id === targetId)) || (targetTitle && (blkTitle === targetTitle)))) {
      targetBlock = blk;
      _matched = true;
      break;
    }
  }
  if (((_matched && targetBlock.reasons) && (targetBlock.reasons.length > 0))) {
    let explanation = "";
    if (window.__skoochServerless) {
      explanation = buildPlacementExplanation(targetBlock);
    } else {
      explanation = window.__skoochJacCall("ai_schedule_reasoning", {"task_title": targetBlock.task_title, "category": targetBlock.category, "day": targetBlock.day, "start_time": targetBlock.start_time, "reasons": targetBlock.reasons});
      explanation = explanation.catch(e => {
        return (("[AI request failed: " + clientErrorBody(e)) + "]");
      });
      explanation = await explanation;
    }
    return (explanation ? String(explanation).trim() : "I found the placement, but I couldn't explain it right now.");
  }
  if (_matched) {
    return (("I placed " + (targetBlock.task_title ? String(targetBlock.task_title) : "it")) + " in a slot that fit your week.");
  }
  for (const ov of _overflow) {
    let ovTitle = ((ov && ov.title) ? String(ov.title).toLowerCase().trim() : "");
    if (((targetId && (ov.id === targetId)) || (targetTitle && (ovTitle === targetTitle)))) {
      return (String(ov.title) + " is still unplaced, so there isn't a scheduled slot to explain yet.");
    }
  }
  return "I couldn't find that item in your current schedule.";
}
async function executeAiDeleteTask(action) {
  let _tasks = getStoreTasks();
  let _fixedBlocks = getStoreFixedBlocks();
  let _schedule = getStoreSchedule();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let searchTitle = _normForMatch((_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : ""));
  if (!searchTitle) {
    return "I couldn't identify which task to delete.";
  }
  let matchTask = findTaskByFuzzyTitle(_tasks, searchTitle, _normForMatch);
  if (!matchTask) {
    let matchBlock = findFixedBlockByFuzzyTitle(_fixedBlocks, searchTitle, _normForMatch);
    if (matchBlock) {
      let _eventId = (matchBlock.task_id ? matchBlock.task_id : matchBlock.id);
      let _isSynced = (matchBlock.source ? (String(matchBlock.source) === "google_calendar") : false);
      let _undoable = false;
      let _delCount = (-1);
      if (_uid) {
        let _tok = await getAccessToken();
        let _ranGcal = false;
        if ((_isSynced && !_isAnon)) {
          try {
            let _del = await _gcalServerlessInvoke("delete_with_undo", {"user_id": _uid, "fixed_event_id": _eventId});
            if (((_del && _del.ok) && ((Number(_del.deleted) || 0) >= 1))) {
              _undoable = true;
              _delCount = (Number(_del.deleted) || 0);
              _ranGcal = true;
            }
          } catch (__jac_e) {
            if (true) {
              let _eDel = __jac_e;
              console.log("gcal_delete_with_undo failed, falling back to plain delete:", _eDel);
            } else {
              throw __jac_e;
            }
          }
        }
        if (!_ranGcal) {
          let _plain = await deleteFixedEventRemote(_uid, _isAnon, _tok, _eventId);
          _delCount = (_jac.poly.and_(_plain, () => ("count" in _plain)) ? (Number(_plain.count) || 0) : 0);
        }
      }
      setStoreFixedBlocks(_fixedBlocks.filter(fb => {
        return ((fb.task_id ? fb.task_id : fb.id) !== _eventId);
      }));
      setStoreSchDirty(true);
      let _effCount = ((_delCount < 0) ? 1 : _delCount);
      return deleteReceipt("event", matchBlock.task_title, _effCount, _undoable);
    }
    return (("I couldn't find a task or event matching '" + String(action.task_title)) + "'.");
  }
  let _taskCount = (-1);
  if (_uid) {
    let _tok = await getAccessToken();
    let _tdel = await deleteTaskRemote(_uid, _isAnon, _tok, matchTask.id);
    if ((_tdel && _jac.poly.contains(_tdel, "count"))) {
      _taskCount = (Number(_tdel.count) || 0);
    } else if ((_tdel && _jac.poly.contains(_tdel, "_sb_error"))) {
      _taskCount = 0;
    } else {
      _taskCount = 1;
    }
  }
  let _remainingTasks = _tasks.filter(t => {
    return (t.id !== matchTask.id);
  });
  setStoreTasks(_remainingTasks);
  setStoreSchedule(_schedule.filter(b => {
    return (b.task_id !== matchTask.id);
  }));
  window.__skoochSyncSchTasksFromPending(_remainingTasks);
  setStoreSchDirty(true);
  let _effTaskCount = ((_taskCount < 0) ? 1 : _taskCount);
  return deleteReceipt("task", matchTask.title, _effTaskCount, false);
}
async function executeAiUndoDelete(action) {
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  if ((!_uid || _isAnon)) {
    return "Calendar undo only works once you're signed in with Google Calendar connected.";
  }
  let searchTitle = _normForMatch((_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : ""));
  let _tok = await getAccessToken();
  let targetId = "";
  let targetTitle = "";
  try {
    let _sb = get_supabase();
    let _q = _sb.from("gcal_event_trash").select("id,fixed_event_row,deleted_at").eq("user_id", _uid);
    let _res = await _q;
    let _rows = ((_res && _res.data) ? _res.data : []);
    let _match = findTrashTombstoneByTitle(_rows, searchTitle, _normForMatch);
    if ((!_match && searchTitle)) {
      return (("I couldn't find a recently-deleted calendar event matching '" + String(action.task_title)) + "' to restore.");
    }
    if (_match) {
      targetId = (_match.id ? String(_match.id) : "");
      targetTitle = trashTombstoneTitle(_match);
    }
  } catch (__jac_e) {
    if (true) {
      let _eRead = __jac_e;
      console.log("undo: trash lookup failed (falling back to most-recent):", _eRead);
    } else {
      throw __jac_e;
    }
  }
  let _undo = null;
  try {
    _undo = await _gcalServerlessInvoke("undo_delete", {"user_id": _uid, "fixed_event_id": targetId});
  } catch (__jac_e) {
    if (true) {
      let _eUndo = __jac_e;
      _undo = {"ok": false, "error": clientErrorBody(_eUndo)};
    } else {
      throw __jac_e;
    }
  }
  if ((!_undo || !_undo.ok)) {
    if ((_undo && _undo.expired)) {
      return "That deletion is too old to undo \u2014 the restore window has expired.";
    }
    return "I couldn't restore that calendar event \u2014 there was nothing to undo, or the restore failed.";
  }
  try {
    await refreshFixedBlocksAfterSync(_uid, _tok);
  } catch (__jac_e) {
    if (true) {
      let _eRefresh = __jac_e;
      console.log("undo: fixed-blocks refresh failed (non-fatal):", _eRefresh);
    } else {
      throw __jac_e;
    }
  }
  setStoreSchDirty(true);
  if ((targetTitle && (targetTitle !== "the deleted event"))) {
    return (("Restored: " + stripTrailingDot(targetTitle)) + ".");
  }
  return "Restored your most recently deleted calendar event.";
}
function _isoToWeekdayMon1(iso) {
  let _s = String((iso ? iso : ""));
  if ((_s.length < 10)) {
    return 0;
  }
  let _dt = Reflect.construct(Date, [(_s.slice(0, 10) + "T00:00:00Z")]);
  let _wd = _dt.getUTCDay();
  if ((_wd !== _wd)) {
    return 0;
  }
  return ((_wd === 0) ? 7 : _wd);
}
function _weekdayPlural(wd) {
  let _names = ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"];
  if (((wd >= 1) && (wd <= 7))) {
    return _names.at((wd - 1));
  }
  return "that day";
}
async function _rescheduleFixedEvent(action, searchTitle, newDate, newTime) {
  let _fixedBlocks = getStoreFixedBlocks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _rawTitle = (_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : "");
  let _match = findFixedBlockByFuzzyTitle(_fixedBlocks, searchTitle, _normForMatch);
  if (!_match) {
    return (("I couldn't find a task or event matching '" + _rawTitle) + "' to move.");
  }
  let _title = (_match.task_title ? String(_match.task_title) : "");
  let _isExternal = (_match.source ? ((String(_match.source) === "google_calendar") || (String(_match.source) === "ical")) : false);
  if (_isExternal) {
    return (("I can't move '" + stripTrailingDot(_title)) + "' \u2014 it's synced from your calendar, so its time is owned there. Edit it in that calendar and it'll sync back.");
  }
  let _isRecurring = ((_match.is_recurring !== undefined) ? _jac.builtin.bool(_match.is_recurring) : false);
  let _eventId = (_match.task_id ? _match.task_id : _match.id);
  let _category = (_match.category ? String(_match.category) : "other");
  let _location = (_match.location ? String(_match.location) : "");
  let _startTime = (_match.start_time ? String(_match.start_time) : "");
  let _endTime = (_match.end_time ? String(_match.end_time) : "");
  let _durMins = (_match.duration_mins ? parseInt(String(_match.duration_mins)) : 0);
  let _scheduledDate = (_match.scheduled_date ? String(_match.scheduled_date) : "");
  let _newStart = "";
  let _newEnd = _endTime;
  if ((newTime && (newTime.indexOf(":") >= 0))) {
    _newStart = newTime;
    if ((_durMins > 0)) {
      _newEnd = _addMinutesToTime(_newStart, _durMins);
    } else if ((_startTime && _endTime)) {
      _newEnd = _addMinutesToTime(_newStart, _minutesBetween(_startTime, _endTime));
    }
  }
  if (_isRecurring) {
    let _newWd = (newDate ? _isoToWeekdayMon1(newDate) : 0);
    if ((_newWd > 0)) {
      let _existingDays = [];
      for (const _b of _fixedBlocks) {
        let _bid = (_b.task_id ? _b.task_id : _b.id);
        if ((((_bid === _eventId) && (_b.day !== undefined)) && (_b.day !== null))) {
          if (!_existingDays.includes(_b.day)) {
            _existingDays.push(_b.day);
          }
        }
      }
      let _useStart = (_newStart ? _newStart : _startTime);
      let _useEnd = (_newStart ? _newEnd : _endTime);
      if (_uid) {
        let _tok = await getAccessToken();
        let _upd = await updateFixedEventRemote(_uid, _isAnon, _tok, _eventId, false, _title, _category, _location, _useStart, _useEnd, _durMins, [_newWd], true, "", _existingDays);
        if (!_upd["ok"]) {
          return (_upd["errorMsg"] ? _upd["errorMsg"] : "I couldn't change that event's day right now.");
        }
      }
      let _kept = _fixedBlocks.filter(_fb => {
        let _fbid2 = (_fb.task_id ? _fb.task_id : _fb.id);
        return (_fbid2 !== _eventId);
      });
      let _newBlk = buildFixedEventBlocks(_eventId, _title, _category, _useStart, _useEnd, _durMins, [_newWd], false);
      setStoreFixedBlocks(_kept.concat(_newBlk));
      setStoreSchDirty(true);
      setStoreSchOptimized(false);
      return (((("Moved your weekly " + stripTrailingDot(_title)) + " to ") + _weekdayPlural(_newWd)) + ".");
    }
    if (!_newStart) {
      return (("'" + stripTrailingDot(_title)) + "' is a recurring event, so I can't move just one occurrence yet. Want me to change which day it repeats on, or delete this series and add a one-off instead?");
    }
  }
  if ((_newStart && !newDate)) {
    let _days = [];
    for (const _b2 of _fixedBlocks) {
      let _bid2 = (_b2.task_id ? _b2.task_id : _b2.id);
      if ((((_bid2 === _eventId) && (_b2.day !== undefined)) && (_b2.day !== null))) {
        if (!_days.includes(_b2.day)) {
          _days.push(_b2.day);
        }
      }
    }
    if (_uid) {
      let _tok = await getAccessToken();
      let _upd = await updateFixedEventRemote(_uid, _isAnon, _tok, _eventId, false, _title, _category, _location, _newStart, _newEnd, _durMins, _days, _isRecurring, _scheduledDate, _days);
      if (!_upd["ok"]) {
        return (_upd["errorMsg"] ? _upd["errorMsg"] : "I couldn't move that event right now.");
      }
    }
    let _updatedBlocksT = _fixedBlocks.map(_fb => {
      let _fbidT = (_fb.task_id ? _fb.task_id : _fb.id);
      if ((_fbidT === _eventId)) {
        _fb["start_time"] = _newStart;
        _fb["end_time"] = _newEnd;
      }
      return _fb;
    });
    setStoreFixedBlocks(_updatedBlocksT);
    setStoreSchDirty(true);
    setStoreSchOptimized(false);
    return (((("Moved '" + stripTrailingDot(_title)) + "' to ") + _newStart) + ".");
  }
  if (!newDate) {
    return (("I couldn't tell which day to move '" + stripTrailingDot(_title)) + "' to. Try naming a day, e.g. 'move it to Thursday'.");
  }
  let _useStartO = (_newStart ? _newStart : _startTime);
  let _useEndO = (_newStart ? _newEnd : _endTime);
  if (_uid) {
    let _tok = await getAccessToken();
    let _upd = await updateFixedEventRemote(_uid, _isAnon, _tok, _eventId, false, _title, _category, _location, _useStartO, _useEndO, _durMins, [], false, newDate, []);
    if (!_upd["ok"]) {
      return (_upd["errorMsg"] ? _upd["errorMsg"] : "I couldn't move that event right now.");
    }
  }
  let _updatedBlocks = _fixedBlocks.map(_fb => {
    let _fbid = (_fb.task_id ? _fb.task_id : _fb.id);
    if ((_fbid === _eventId)) {
      _fb["scheduled_date"] = newDate;
      if (_newStart) {
        _fb["start_time"] = _useStartO;
        _fb["end_time"] = _useEndO;
      }
    }
    return _fb;
  });
  setStoreFixedBlocks(_updatedBlocks);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  return (((("Moved '" + stripTrailingDot(_title)) + "' to ") + formatDayDate(newDate)) + ".");
}
function _findSeriesOccurrence(fixedBlocks, searchTitle, occDate) {
  if (!_jac.builtin.bool(fixedBlocks)) {
    return null;
  }
  let _normDate = (occDate ? String(occDate).slice(0, 10) : "");
  let _anyTitleMatch = null;
  for (const _b of fixedBlocks) {
    let _t = _normForMatch((_b.task_title ? _b.task_title : ""));
    let _titleHit = ((_t === searchTitle) || (_t.indexOf(searchTitle) >= 0));
    if (!_titleHit) {
      continue;
    }
    if (!_anyTitleMatch) {
      _anyTitleMatch = _b;
    }
    let _bd = (_b.scheduled_date ? String(_b.scheduled_date).slice(0, 10) : "");
    if ((_normDate && (_bd === _normDate))) {
      let _rr = (_b.rrule ? _b.rrule : "");
      if (_rr) {
        return _b;
      }
      if ((!_anyTitleMatch || !_anyTitleMatch.rrule)) {
        _anyTitleMatch = _b;
      }
    }
  }
  return _anyTitleMatch;
}
async function executeAiSkipOccurrence(action) {
  let _fixedBlocks = getStoreFixedBlocks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _rawTitle = (_jac.poly.and_(action, () => action.series_title) ? String(action.series_title).trim() : (_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : ""));
  let _occDate = (_jac.poly.and_(action, () => action.occurrence_date) ? String(action.occurrence_date).trim() : "");
  if (!_rawTitle) {
    return "Which recurring event should I skip? Tell me its name and the date.";
  }
  if (!_occDate) {
    return (("Which date should I skip for '" + stripTrailingDot(_rawTitle)) + "'?");
  }
  let _searchTitle = _normForMatch(_rawTitle);
  let _occ = _findSeriesOccurrence(_fixedBlocks, _searchTitle, _occDate);
  if (!_occ) {
    return (((("I couldn't find '" + stripTrailingDot(_rawTitle)) + "' on ") + formatDayDate(_occDate)) + " to skip.");
  }
  let _title = (_occ.task_title ? String(_occ.task_title) : _rawTitle);
  let _rrule = (_occ.rrule ? String(_occ.rrule) : "");
  let _seriesUid = (_occ.uid ? String(_occ.uid) : "");
  let _occIso = (_occ.dtstart_utc ? String(_occ.dtstart_utc) : "");
  if (((!_rrule || !_seriesUid) || !_occIso)) {
    return (("'" + stripTrailingDot(_title)) + "' is a weekly repeat without a per-date model, so I can't skip just one occurrence yet. I can change which day it repeats on, or you can delete this series and add the others as one-offs.");
  }
  if (!_uid) {
    return "Sign in to edit your recurring events.";
  }
  let _tok = await getAccessToken();
  if (_isAnon) {
    try {
      let _sb = get_supabase();
      let _masterResp = await _sb.from("fixed_events").select("id,recurrence_id,exdates").eq("user_id", _uid).eq("uid", _seriesUid);
      if ((_masterResp && _masterResp.error)) {
        return "I couldn't update that series right now.";
      }
      let _allRows = ((_masterResp && _masterResp.data) ? _masterResp.data : []);
      let _master = null;
      for (const _mr of _allRows) {
        if (!_mr.recurrence_id) {
          _master = _mr;
        }
      }
      if (!_master) {
        return "I couldn't find the series to skip that occurrence.";
      }
      let _exdates = (_master.exdates ? _master.exdates : []);
      if (!_exdates.includes(_occIso)) {
        _exdates = _exdates.concat([_occIso]);
      }
      let _wr = await _jac.poly.call(_sb.from("fixed_events"), "update", {"exdates": _exdates, "manually_edited_at": Reflect.construct(Date, []).toISOString()}).eq("id", _master.id).eq("user_id", _uid);
      if ((_wr && _wr.error)) {
        return "I couldn't update that series right now.";
      }
    } catch (__jac_e) {
      if (true) {
        let _ex = __jac_e;
        console.log("anon executeAiSkipOccurrence failed:", _ex);
        return "I couldn't skip that occurrence right now.";
      } else {
        throw __jac_e;
      }
    }
  } else {
    let _res = window.__skoochJacCall("skip_occurrence", {"user_id": _uid, "uid": _seriesUid, "occurrence_iso": _occIso, "access_token": _tok});
    _res = _res.catch(e => {
      return {"_sb_error": "client", "_sb_body": clientErrorBody(e)};
    });
    let _r = await _res;
    if (schedulerRequestFailed(_r)) {
      return schedulerErrorText("I couldn't skip that occurrence", _r);
    }
    if ((_r && (_r.ok === false))) {
      if (_r._unsupported) {
        return (("'" + stripTrailingDot(_title)) + "' doesn't support skipping a single date yet.");
      }
      return (_r.error ? String(_r.error) : "I couldn't skip that occurrence.");
    }
  }
  let _occId = (_occ.id ? String(_occ.id) : "");
  let _kept = _fixedBlocks.filter(_fb => {
    return ((_fb.id ? String(_fb.id) : "") !== _occId);
  });
  setStoreFixedBlocks(_kept);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  try {
    pushGcalAfterLocalChange();
  } catch (__jac_e) {
    if (true) {
      let _epg = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  return (((("Skipped " + stripTrailingDot(_title)) + " on ") + formatDayDate(_occDate)) + ".");
}
async function executeAiMoveOccurrence(action) {
  let _fixedBlocks = getStoreFixedBlocks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _rawTitle = (_jac.poly.and_(action, () => action.series_title) ? String(action.series_title).trim() : (_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : ""));
  let _occDate = (_jac.poly.and_(action, () => action.occurrence_date) ? String(action.occurrence_date).trim() : "");
  let _newStart = (_jac.poly.and_(action, () => action.new_start) ? String(action.new_start).trim() : "");
  let _newDate = (_jac.poly.and_(action, () => action.new_date) ? String(action.new_date).trim() : "");
  if (!_rawTitle) {
    return "Which recurring event should I move? Tell me its name and which date.";
  }
  if (!_occDate) {
    return (("Which date's '" + stripTrailingDot(_rawTitle)) + "' should I move?");
  }
  if ((!_newStart && !_newDate)) {
    return (((("Where should I move '" + stripTrailingDot(_rawTitle)) + "' on ") + formatDayDate(_occDate)) + "? Give me a new time or day.");
  }
  let _searchTitle = _normForMatch(_rawTitle);
  let _occ = _findSeriesOccurrence(_fixedBlocks, _searchTitle, _occDate);
  if (!_occ) {
    return (((("I couldn't find '" + stripTrailingDot(_rawTitle)) + "' on ") + formatDayDate(_occDate)) + " to move.");
  }
  let _title = (_occ.task_title ? String(_occ.task_title) : _rawTitle);
  let _rrule = (_occ.rrule ? String(_occ.rrule) : "");
  let _seriesUid = (_occ.uid ? String(_occ.uid) : "");
  let _occIso = (_occ.dtstart_utc ? String(_occ.dtstart_utc) : "");
  if (((!_rrule || !_seriesUid) || !_occIso)) {
    return (("'" + stripTrailingDot(_title)) + "' is a weekly repeat without a per-date model, so I can't move just one occurrence yet. I can change which day the whole series repeats on instead.");
  }
  let _moved = computeMovedOccurrenceInstants(_occ, _newDate, _newStart);
  if ((!_moved.ok || !_moved.dtstart_utc)) {
    return (("I couldn't work out the new time for '" + stripTrailingDot(_title)) + "'.");
  }
  let _newUtcStart = String(_moved.dtstart_utc);
  let _newUtcEnd = (_moved.dtend_utc ? String(_moved.dtend_utc) : "");
  let _useStart = String(_moved.new_start);
  let _useDate = String(_moved.new_date);
  if (!_uid) {
    return "Sign in to edit your recurring events.";
  }
  let _tok = await getAccessToken();
  if (_isAnon) {
    try {
      let _sb = get_supabase();
      let _occTaskId = (_occ.task_id ? _occ.task_id : (_occ.id ? _occ.id : _seriesUid));
      let _occDur = (_occ.duration_mins ? parseInt(String(_occ.duration_mins)) : 0);
      let _ovId = (((_occ.id ? String(_occ.id) : _seriesUid) + "::override::") + _occIso);
      let _ovRow = {"id": _ovId, "user_id": _uid, "task_id": _occTaskId, "task_title": (_occ.task_title ? _occ.task_title : _title), "category": (_occ.category ? _occ.category : "other"), "location": (_occ.location ? _occ.location : ""), "duration_mins": _occDur, "is_recurring": false, "rrule": null, "recurrence_id": _occIso, "parent_uid": _seriesUid, "uid": _seriesUid, "source": (_occ.source ? _occ.source : "manual"), "source_calendar": (_occ.source_calendar ? _occ.source_calendar : null), "is_all_day": (_occ.is_all_day ? true : false), "status": "CONFIRMED", "dtstart_utc": _newUtcStart, "dtend_utc": _newUtcEnd, "start_time": _useStart, "end_time": ((_useStart && _occDur) ? _addMinutesToTime(_useStart, _occDur) : (_occ.end_time ? String(_occ.end_time) : "")), "scheduled_date": _useDate};
      let _ins = await _sb.from("fixed_events").upsert([_ovRow], {"onConflict": "user_id,uid,recurrence_id"});
      if ((_ins && _ins.error)) {
        return "I couldn't move that occurrence right now.";
      }
    } catch (__jac_e) {
      if (true) {
        let _ex = __jac_e;
        console.log("anon executeAiMoveOccurrence failed:", _ex);
        return "I couldn't move that occurrence right now.";
      } else {
        throw __jac_e;
      }
    }
  } else {
    let _res = window.__skoochJacCall("move_occurrence", {"user_id": _uid, "uid": _seriesUid, "occurrence_iso": _occIso, "new_dtstart_iso": _newUtcStart, "new_dtend_iso": _newUtcEnd, "new_start_hhmm": _useStart, "new_end_hhmm": (_useStart ? _addMinutesToTime(_useStart, (_occ.duration_mins ? parseInt(String(_occ.duration_mins)) : 0)) : ""), "new_scheduled_date": _useDate, "access_token": _tok});
    _res = _res.catch(e => {
      return {"_sb_error": "client", "_sb_body": clientErrorBody(e)};
    });
    let _r = await _res;
    if (schedulerRequestFailed(_r)) {
      return schedulerErrorText("I couldn't move that occurrence", _r);
    }
    if ((_r && (_r.ok === false))) {
      if (_r._unsupported) {
        return (("'" + stripTrailingDot(_title)) + "' doesn't support moving a single date yet.");
      }
      return (_r.error ? String(_r.error) : "I couldn't move that occurrence.");
    }
  }
  let _occId = (_occ.id ? String(_occ.id) : "");
  let _updated = _fixedBlocks.map(_fb => {
    if (((_fb.id ? String(_fb.id) : "") === _occId)) {
      if (_useDate) {
        _fb["scheduled_date"] = _useDate;
      }
      if (_useStart) {
        let _dm = (_fb.duration_mins ? parseInt(String(_fb.duration_mins)) : 0);
        _fb["start_time"] = _useStart;
        if ((_dm > 0)) {
          _fb["end_time"] = _addMinutesToTime(_useStart, _dm);
        }
      }
      _fb["dtstart_utc"] = _newUtcStart;
      if (_newUtcEnd) {
        _fb["dtend_utc"] = _newUtcEnd;
      }
    }
    return _fb;
  });
  setStoreFixedBlocks(_updated);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  try {
    pushGcalAfterLocalChange();
  } catch (__jac_e) {
    if (true) {
      let _epg = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _whenLbl = (_newDate ? (formatDayDate(_useDate) + (_useStart ? (" at " + _useStart) : "")) : (_useStart ? ("to " + _useStart) : "the new time"));
  return (((((("Moved " + stripTrailingDot(_title)) + " on ") + formatDayDate(_occDate)) + " to ") + _whenLbl) + ".");
}
async function executeAiRescheduleTask(action) {
  let _tasks = getStoreTasks();
  let _schedule = getStoreSchedule();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let searchTitle = _normForMatch((_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : ""));
  if (!searchTitle) {
    return "I couldn't identify which task to move.";
  }
  let newDate = (_jac.poly.and_(action, () => action.preferred_date) ? String(action.preferred_date).trim() : "");
  let newTime = (_jac.poly.and_(action, () => action.preferred_time) ? String(action.preferred_time).trim() : "");
  let newDeadline = (_jac.poly.and_(action, () => action.deadline) ? String(action.deadline).trim() : "");
  if ((!newDate && !newTime)) {
    return "I couldn't tell when to move that to. Try naming a day or time, e.g. 'move it to tomorrow morning'.";
  }
  let matchTask = findTaskByFuzzyTitle(_tasks, searchTitle, _normForMatch);
  if (!matchTask) {
    return await _rescheduleFixedEvent(action, searchTitle, newDate, newTime);
  }
  let _existingDeadline = (matchTask.deadline ? String(matchTask.deadline).trim() : "");
  let _effDeadline = (newDeadline ? newDeadline : _existingDeadline);
  if ((((newDate && !newDeadline) && _existingDeadline) && (newDate > _existingDeadline))) {
    _effDeadline = newDate;
  }
  let _beforeMap = snapshotPlacements(_schedule);
  let _updatedTasks = applyRescheduleUpdates(_tasks, matchTask.id, newDate, newTime, _effDeadline);
  setStoreTasks(_updatedTasks);
  if ((_uid && _effDeadline)) {
    let _rsTok = await getAccessToken();
    await updateTaskFullRemote(_uid, _isAnon, _rsTok, matchTask.id, matchTask.title, (matchTask.category ? matchTask.category : "other"), (matchTask.duration_mins ? matchTask.duration_mins : 30), (matchTask.priority ? matchTask.priority : 5), _effDeadline);
  }
  setStoreSchedule(_schedule.filter(b => {
    return (b.task_id !== matchTask.id);
  }));
  window.__skoochSyncSchTasksFromPending(_updatedTasks);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  await runOptimize();
  let _weekIsoRs = (window.__skoochWeekIsoDays ? window.__skoochWeekIsoDays : []);
  let _todayIsoRs = _aiDateToIso(Reflect.construct(Date, []));
  let _targetBits = [];
  if (newDate) {
    _targetBits.push(newDate);
  }
  if (newTime) {
    _targetBits.push(newTime);
  }
  let _targetTxt = ((_targetBits.length > 0) ? ("to " + _jac.poly.call(_targetBits, "join", " ")) : "");
  let _intendedRs = {"kind": "task", "title": matchTask.title, "preferredDate": newDate, "preferredTime": newTime, "taskDeadline": _effDeadline};
  let _moveMsg = describeMoveOutcome(stripTrailingDot(matchTask.title), matchTask.id, _beforeMap, getStoreSchedule(), getStoreOverflow(), _intendedRs, _weekIsoRs, _todayIsoRs, _targetTxt);
  let _caveat = "";
  if ((window.__skoochIsAnon !== false)) {
    _caveat = " (This is a preview \u2014 sign up and finish the quick setup for fully optimized scheduling.)";
  } else if (!(window.__skoochProfileComplete === true)) {
    _caveat = " (This is a preview \u2014 finish the quick survey for fully optimized scheduling.)";
  }
  return (_moveMsg + _caveat);
}
async function _resizeFlexTask(action, searchRaw, searchTitle) {
  let _tasks = getStoreTasks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _matchTask = findTaskByFuzzyTitle(_tasks, searchTitle, _normForMatch);
  if (!_matchTask) {
    return (("I couldn't find a fixed event or task matching '" + searchRaw) + "'.");
  }
  let _newDurArg = (_jac.poly.and_(action, () => action.duration_mins) ? parseInt(String(action.duration_mins)) : 0);
  let _newDur = 0;
  if ((_newDurArg > 0)) {
    _newDur = _newDurArg;
  } else {
    let _newEnd = (_jac.poly.and_(action, () => action.new_end_time) ? String(action.new_end_time).trim() : "");
    let _prefTime = (_matchTask.preferred_time ? String(_matchTask.preferred_time).trim() : "");
    if ((((_newEnd && _prefTime) && (_prefTime.indexOf(":") >= 0)) && (_newEnd.indexOf(":") >= 0))) {
      _newDur = _minutesBetween(_prefTime, _newEnd);
    }
  }
  if ((_newDur <= 0)) {
    return (("I couldn't resize '" + stripTrailingDot(String(_matchTask.title))) + "' \u2014 tell me a new duration (e.g. 'make it 90 minutes').");
  }
  let _updatedTasks = _tasks.map(_t => {
    if ((_t.id === _matchTask.id)) {
      _t["duration_mins"] = _newDur;
    }
    return _t;
  });
  setStoreTasks(_updatedTasks);
  if (_uid) {
    let _tok = await getAccessToken();
    await updateTaskFullRemote(_uid, _isAnon, _tok, _matchTask.id, _matchTask.title, (_matchTask.category ? _matchTask.category : "other"), _newDur, (_matchTask.priority ? _matchTask.priority : 5), (_matchTask.deadline ? String(_matchTask.deadline).trim() : ""));
  }
  let _schedule = getStoreSchedule();
  setStoreSchedule(_schedule.filter(b => {
    return (b.task_id !== _matchTask.id);
  }));
  window.__skoochSyncSchTasksFromPending(_updatedTasks);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  await runOptimize();
  let _durLbl = formatDurationMins(_newDur);
  return (((("Made '" + stripTrailingDot(String(_matchTask.title))) + "' ") + (_durLbl ? _durLbl : (String(_newDur) + " min"))) + " long.");
}
async function executeAiResizeEvent(action) {
  let _fixedBlocks = getStoreFixedBlocks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _searchRaw = (_jac.poly.and_(action, () => action.event_title) ? String(action.event_title).trim() : "");
  let _searchTitle = _normForMatch(_searchRaw);
  if (!_searchTitle) {
    return "I couldn't tell which event to resize.";
  }
  let _match = findFixedBlockByFuzzyTitle(_fixedBlocks, _searchTitle, _normForMatch);
  if (!_match) {
    return await _resizeFlexTask(action, _searchRaw, _searchTitle);
  }
  let _eventId = (_match.task_id ? _match.task_id : _match.id);
  let _isExternal = (_match.source ? ((String(_match.source) === "google_calendar") || (String(_match.source) === "ical")) : false);
  if (_isExternal) {
    return (("I can't resize '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 it's synced from your calendar, so its time is owned there. Edit it in that calendar and it'll sync back.");
  }
  let _startTime = (_match.start_time ? String(_match.start_time) : "");
  if (!_startTime) {
    return (("I couldn't resize '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 it has no start time to measure from.");
  }
  let _newEnd = (_jac.poly.and_(action, () => action.new_end_time) ? String(action.new_end_time).trim() : "");
  let _newDurArg = (_jac.poly.and_(action, () => action.duration_mins) ? parseInt(String(action.duration_mins)) : 0);
  if ((!_newEnd && (_newDurArg > 0))) {
    _newEnd = _addMinutesToTime(_startTime, _newDurArg);
  }
  if (!_newEnd) {
    return (("I couldn't resize '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 tell me a new end time or duration.");
  }
  let _newDurMins = _minutesBetween(_startTime, _newEnd);
  if ((_newDurMins <= 0)) {
    return (((((("I couldn't resize '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 the new end (") + _newEnd) + ") isn't after the start (") + _startTime) + ").");
  }
  let _isRecurring = ((_match.is_recurring !== undefined) ? _jac.builtin.bool(_match.is_recurring) : false);
  let _scheduledDate = (_match.scheduled_date ? String(_match.scheduled_date) : "");
  let _category = (_match.category ? String(_match.category) : "other");
  let _title = (_match.task_title ? String(_match.task_title) : "");
  let _location = (_match.location ? String(_match.location) : "");
  let _days = [];
  for (const _b of _fixedBlocks) {
    let _bid = (_b.task_id ? _b.task_id : _b.id);
    if ((((_bid === _eventId) && (_b.day !== undefined)) && (_b.day !== null))) {
      if (!_days.includes(_b.day)) {
        _days.push(_b.day);
      }
    }
  }
  if (_uid) {
    let _tok = await getAccessToken();
    let _upd = await updateFixedEventRemote(_uid, _isAnon, _tok, _eventId, false, _title, _category, _location, _startTime, _newEnd, _newDurMins, _days, _isRecurring, _scheduledDate, _days);
    if (!_upd["ok"]) {
      return (_upd["errorMsg"] ? _upd["errorMsg"] : "I couldn't save that change right now.");
    }
  }
  let _updatedBlocks = _fixedBlocks.map(_fb => {
    let _fbid = (_fb.task_id ? _fb.task_id : _fb.id);
    if ((_fbid === _eventId)) {
      _fb["end_time"] = _newEnd;
      _fb["duration_mins"] = _newDurMins;
    }
    return _fb;
  });
  setStoreFixedBlocks(_updatedBlocks);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  return (((("Resized '" + stripTrailingDot(_title)) + "' to end at ") + _newEnd) + ".");
}
async function executeAiSplitEvent(action) {
  let _fixedBlocks = getStoreFixedBlocks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _searchRaw = (_jac.poly.and_(action, () => action.event_title) ? String(action.event_title).trim() : "");
  let _searchTitle = _normForMatch(_searchRaw);
  if (!_searchTitle) {
    return "I couldn't tell which event to split.";
  }
  let _match = findFixedBlockByFuzzyTitle(_fixedBlocks, _searchTitle, _normForMatch);
  if (!_match) {
    return (("I couldn't find a fixed event matching '" + _searchRaw) + "'.");
  }
  let _eventId = (_match.task_id ? _match.task_id : _match.id);
  let _isExternal = (_match.source ? ((String(_match.source) === "google_calendar") || (String(_match.source) === "ical")) : false);
  if (_isExternal) {
    return (("I can't split '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 it's synced from your calendar, so its time is owned there. Edit it in that calendar and it'll sync back.");
  }
  let _startTime = (_match.start_time ? String(_match.start_time) : "");
  let _endTime = (_match.end_time ? String(_match.end_time) : "");
  let _splitAt = (_jac.poly.and_(action, () => action.split_at_time) ? String(action.split_at_time).trim() : "");
  if ((!_startTime || !_endTime)) {
    return (("I couldn't split '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 it doesn't have a start and end time.");
  }
  if (!_splitAt) {
    return (("I couldn't split '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 tell me the time to split it at.");
  }
  let _startMins = _hhmm_to_mins(_startTime);
  let _endMins = _hhmm_to_mins(_endTime);
  let _splitMins = _hhmm_to_mins(_splitAt);
  if (((_splitMins <= _startMins) || (_splitMins >= _endMins))) {
    return (((((((("I couldn't split '" + stripTrailingDot(String(_match.task_title))) + "' at ") + _splitAt) + " \u2014 that time isn't inside ") + _startTime) + "-") + _endTime) + ".");
  }
  let _insertTitle = (_jac.poly.and_(action, () => action.insert_title) ? String(action.insert_title).trim() : "");
  let _insertDur = (_jac.poly.and_(action, () => action.insert_duration_mins) ? parseInt(String(action.insert_duration_mins)) : 0);
  let _hasInsert = (_insertTitle ? true : false);
  if ((_hasInsert && (_insertDur <= 0))) {
    _insertDur = 60;
  }
  let _insertCategory = normalizeAiCategory((_jac.poly.and_(action, () => action.insert_category) ? String(action.insert_category) : "other"));
  let _part2Start = (_hasInsert ? _addMinutesToTime(_splitAt, _insertDur) : _splitAt);
  if ((_hhmm_to_mins(_part2Start) >= _endMins)) {
    return (((((((("I couldn't split '" + stripTrailingDot(String(_match.task_title))) + "' \u2014 the inserted ") + (_insertTitle ? _insertTitle : "block")) + " (") + String(_insertDur)) + " min) wouldn't fit before ") + _endTime) + ".");
  }
  let _isRecurring = ((_match.is_recurring !== undefined) ? _jac.builtin.bool(_match.is_recurring) : false);
  let _scheduledDate = (_match.scheduled_date ? String(_match.scheduled_date) : "");
  let _category = (_match.category ? String(_match.category) : "other");
  let _title = (_match.task_title ? String(_match.task_title) : "");
  let _location = (_match.location ? String(_match.location) : "");
  let _days = [];
  for (const _b of _fixedBlocks) {
    let _bid = (_b.task_id ? _b.task_id : _b.id);
    if ((((_bid === _eventId) && (_b.day !== undefined)) && (_b.day !== null))) {
      if (!_days.includes(_b.day)) {
        _days.push(_b.day);
      }
    }
  }
  let _part1Dur = _minutesBetween(_startTime, _splitAt);
  let _part2Dur = _minutesBetween(_part2Start, _endTime);
  let _part2Title = (_title + " (cont.)");
  let _accessToken = "";
  if (_uid) {
    _accessToken = await getAccessToken();
    let _r1 = await updateFixedEventRemote(_uid, _isAnon, _accessToken, _eventId, false, _title, _category, _location, _startTime, _splitAt, _part1Dur, _days, _isRecurring, _scheduledDate, _days);
    if (!_r1["ok"]) {
      return (_r1["errorMsg"] ? _r1["errorMsg"] : "I couldn't split that event right now.");
    }
    let _r2 = await createFixedEventRemote(_uid, _isAnon, _accessToken, _part2Title, _category, _location, _part2Start, _endTime, _part2Dur, _days, _isRecurring, _scheduledDate, false);
    if (!_r2["ok"]) {
      return (_r2["errorMsg"] ? _r2["errorMsg"] : (("I shortened '" + stripTrailingDot(_title)) + "' but couldn't add the second part."));
    }
  }
  let _insertBlocks = [];
  if ((_hasInsert && _uid)) {
    let _insertEnd = _addMinutesToTime(_splitAt, _insertDur);
    let _ri = await createFixedEventRemote(_uid, _isAnon, _accessToken, _insertTitle, _insertCategory, "", _splitAt, _insertEnd, _insertDur, _days, _isRecurring, _scheduledDate, false);
    if (!_ri["ok"]) {
      return (_ri["errorMsg"] ? _ri["errorMsg"] : (((("I split '" + stripTrailingDot(_title)) + "' but couldn't add ") + _insertTitle) + "."));
    }
    _insertBlocks = (_ri["blocks"] ? _ri["blocks"] : []);
  }
  let _part2Id = nextAiLocalId("fe-local-");
  let _part2Blocks = buildFixedEventBlocks(_part2Id, _part2Title, _category, _part2Start, _endTime, _part2Dur, _days, false);
  let _resizedBlocks = _fixedBlocks.map(_fb => {
    let _fbid = (_fb.task_id ? _fb.task_id : _fb.id);
    if ((_fbid === _eventId)) {
      _fb["end_time"] = _splitAt;
      _fb["duration_mins"] = _part1Dur;
    }
    return _fb;
  });
  let _newBlocks = _resizedBlocks.concat(_part2Blocks);
  if (_hasInsert) {
    let _toAppend = _insertBlocks;
    if ((_toAppend.length === 0)) {
      _toAppend = buildFixedEventBlocks(nextAiLocalId("fe-local-"), _insertTitle, _insertCategory, _splitAt, _addMinutesToTime(_splitAt, _insertDur), _insertDur, _days, false);
    }
    _newBlocks = _newBlocks.concat(_toAppend);
  }
  setStoreFixedBlocks(_newBlocks);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  if (_hasInsert) {
    return (((((("Split '" + stripTrailingDot(_title)) + "' at ") + _splitAt) + "; added ") + _insertTitle) + " in between.");
  }
  return (((("Split '" + stripTrailingDot(_title)) + "' into two parts at ") + _splitAt) + ".");
}
async function executeAiDeleteAllTasks() {
  let _tasks = getStoreTasks();
  let _fixedBlocksDA = getStoreFixedBlocks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _hadAnything = ((_tasks.length > 0) || (_fixedBlocksDA.length > 0));
  let _confirmDeleteAll = true;
  try {
    _confirmDeleteAll = window.confirm("Delete ALL your tasks, events, and schedule? This can't be undone.");
  } catch (__jac_e) {
    if (true) {
      let _cfErr = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  if (!_confirmDeleteAll) {
    return "Okay \u2014 I left everything as is. Nothing was deleted.";
  }
  if (_uid) {
    let _tok = await getAccessToken();
    if (_isAnon) {
      try {
        let _sbDA = get_supabase();
        await _sbDA.from("tasks").delete().eq("user_id", _uid).neq("id", "NEVER_MATCH");
        await _sbDA.from("fixed_events").delete().eq("user_id", _uid).neq("id", "NEVER_MATCH");
        await _sbDA.from("schedule_blocks").delete().eq("user_id", _uid).neq("id", "NEVER_MATCH");
      } catch (__jac_e) {
        if (true) {
          let _daEx = __jac_e;
          console.log("anon delete-all failed:", _daEx);
        } else {
          throw __jac_e;
        }
      }
    } else {
      for (const t of _tasks) {
        await deleteTaskRemote(_uid, false, _tok, t.id);
      }
      await deleteAllFixedEventsRemote(_uid, false, _tok);
    }
  }
  setStoreTasks([]);
  setStoreFixedBlocks([]);
  setStoreSchedule([]);
  setStoreSchedulerTasks([]);
  setStoreOverflow([]);
  setStoreSchOptimized(false);
  setStoreSchDirty(true);
  if (!_hadAnything) {
    return "Your schedule was already empty \u2014 there was nothing to delete.";
  }
  return "Deleted all tasks and fixed events.";
}
async function autoAcceptAiTask(action, sourcePrompt = "") {
  let res = await executeAiCreateTaskResult(action, sourcePrompt);
  if (!res.ok) {
    return res.msg;
  }
  return res.msg;
}
async function executeAiAcceptAllPlacements() {
  window.__skoochOpInFlight = true;
  let _schedule = getStoreSchedule();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _tasks = getStoreTasks();
  let _accept = computeAcceptAllPlacements(_schedule);
  if ((_accept["affectedTaskIds"].length === 0)) {
    window.__skoochOpInFlight = false;
    return "There are no proposed placements to accept.";
  }
  setStoreSchedule(_accept["newSchedule"]);
  let okSave = await upsertScheduleBlocks(_accept["changed"], "Couldn't accept placements");
  if (!okSave) {
    window.__skoochOpInFlight = false;
    return "Couldn't save accepted placements \u2014 they remain proposed.";
  }
  if (_uid) {
    let accessToken = await getAccessToken();
    for (const tid of _accept["affectedTaskIds"]) {
      await updateTaskStatusOnlyRemote(_uid, _isAnon, accessToken, tid, "accepted");
    }
  }
  let _newTasks = computeAcceptedStatusTasks(_tasks, _accept["affectedTaskIds"]);
  setStoreTasks(_newTasks);
  window.__skoochSyncSchTasksFromPending(_newTasks);
  let _n = _accept["affectedTaskIds"].length;
  try {
    pushGcalAfterLocalChange();
  } catch (__jac_e) {
    if (true) {
      let _epg = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  window.__skoochOpInFlight = false;
  if ((_n > 0)) {
    window.__skoochOpenClaimModal("schedule-accepted");
  }
  return (((("Accepted " + String(_n)) + " placement") + ((_n !== 1) ? "s" : "")) + ".");
}
async function _replanFixedEvent(action, searchTitle) {
  let _fixedBlocks = getStoreFixedBlocks();
  let _rawTitle = (_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : (_jac.poly.and_(action, () => action.task_ref) ? String(action.task_ref).trim() : ""));
  let _match = findFixedBlockByFuzzyTitle(_fixedBlocks, searchTitle, _normForMatch);
  if (!_match) {
    return (("I couldn't find a task or event matching '" + _rawTitle) + "' to re-plan.");
  }
  let _title = (_match.task_title ? String(_match.task_title) : "");
  let _delta = (_jac.poly.and_(action, () => action.delta) ? action.delta : action);
  let _dir = ((_delta && _delta.direction) ? String(_delta.direction).toLowerCase().trim() : "");
  let _tgtTime = ((_delta && _delta.target_time) ? String(_delta.target_time).trim() : "");
  let _tgtDay = ((_delta && _delta.target_day) ? String(_delta.target_day).trim() : "");
  if ((_tgtTime || _tgtDay)) {
    let _fwd = {"task_title": _title, "preferred_time": _tgtTime, "preferred_date": _tgtDay};
    return await _rescheduleFixedEvent(_fwd, searchTitle, _tgtDay, _tgtTime);
  }
  if (((_dir === "earlier") || (_dir === "later"))) {
    let _curStart = (_match.start_time ? String(_match.start_time) : "");
    if ((!_curStart || (_curStart.indexOf(":") < 0))) {
      return (("I can't shift '" + stripTrailingDot(_title)) + "' \u2014 tell me a specific new time and I'll move it.");
    }
    let _shift = 60;
    let _magRaw = (((_delta && (_delta.minutes !== undefined)) && (_delta.minutes !== null)) ? parseInt(String(_delta.minutes)) : 0);
    if (((_magRaw === _magRaw) && (_magRaw > 0))) {
      _shift = _magRaw;
    }
    if ((_shift < 5)) {
      _shift = 5;
    }
    if ((_shift > 720)) {
      _shift = 720;
    }
    let _baseMins = _hhmm_to_mins(_curStart);
    let _newMins = ((_dir === "earlier") ? (_baseMins - _shift) : (_baseMins + _shift));
    if ((_newMins < 420)) {
      _newMins = 420;
    }
    if ((_newMins > 1320)) {
      _newMins = 1320;
    }
    let _newStart = _addMinutesToTime("00:00", _newMins);
    let _fwd2 = {"task_title": _title, "preferred_time": _newStart};
    return await _rescheduleFixedEvent(_fwd2, searchTitle, "", _newStart);
  }
  return (("'" + stripTrailingDot(_title)) + "' is a fixed event \u2014 tell me a specific new time and I'll move it.");
}
async function executeAiReplan(action) {
  let _tasks = getStoreTasks();
  let _schedule = getStoreSchedule();
  let searchTitle = _normForMatch((_jac.poly.and_(action, () => action.task_title) ? String(action.task_title).trim() : (_jac.poly.and_(action, () => action.task_ref) ? String(action.task_ref).trim() : "")));
  if (!searchTitle) {
    return "I couldn't tell which task to re-plan \u2014 name it and I'll shift it.";
  }
  let matchTask = findTaskByFuzzyTitle(_tasks, searchTitle, _normForMatch);
  if (!matchTask) {
    return await _replanFixedEvent(action, searchTitle);
  }
  let _weekIsoRp = (window.__skoochWeekIsoDays ? window.__skoochWeekIsoDays : []);
  let _todayIsoRp = _aiDateToIso(Reflect.construct(Date, []));
  let _beforeMap = snapshotPlacements(_schedule);
  let _current = _beforeMap[String(matchTask.id)];
  if ((_current === undefined)) {
    _current = null;
  }
  if ((_current === null)) {
    _current = {};
  }
  if ((!_current.preferredTime && matchTask.preferred_time)) {
    _current["preferredTime"] = String(matchTask.preferred_time);
  }
  let _delta = (_jac.poly.and_(action, () => action.delta) ? action.delta : action);
  let _tr = translateReplanDelta(_delta, _current, _weekIsoRp);
  if (!_tr["ok"]) {
    return _tr["error"];
  }
  let _updatedTasks = applyRescheduleUpdates(_tasks, matchTask.id, _tr["preferredDate"], _tr["preferredTime"], "");
  setStoreTasks(_updatedTasks);
  setStoreSchedule(_schedule.filter(b => {
    return (b.task_id !== matchTask.id);
  }));
  window.__skoochSyncSchTasksFromPending(_updatedTasks);
  setStoreSchDirty(true);
  setStoreSchOptimized(false);
  await runOptimize();
  let _intendedRp = {"kind": "task", "title": matchTask.title, "preferredDate": _tr["preferredDate"], "preferredTime": _tr["preferredTime"]};
  return describeMoveOutcome(stripTrailingDot(matchTask.title), matchTask.id, _beforeMap, getStoreSchedule(), getStoreOverflow(), _intendedRp, _weekIsoRp, _todayIsoRp, _tr["describe"]);
}
async function executeAiAction(action, sourcePrompt = "") {
  if ((!_jac.builtin.bool(action) || !action.type)) {
    return "";
  }
  if ((action.type !== "create_task")) {
    try {
      logInteractionEvent("chat_action_executed", {"entity_id": (action.task_id ? action.task_id : (action.id ? action.id : null)), "category": (action.category ? action.category : null), "payload": {"action_type": action.type, "action": action, "source_prompt": sourcePrompt}});
    } catch (__jac_e) {
      if (true) {
        let _ele = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  }
  if ((action.type === "create_task")) {
    return await executeAiCreateTask(action, sourcePrompt);
  }
  if ((action.type === "create_fixed_event")) {
    return await executeAiCreateFixedEvent(action);
  }
  if ((action.type === "add_rule")) {
    return await executeAiAddRule(action);
  }
  if ((action.type === "set_gap")) {
    return await executeAiSetGap(action);
  }
  if ((action.type === "repeat_task")) {
    return await executeAiRepeatTask(action);
  }
  if ((action.type === "rebalance_week")) {
    return await executeAiRebalanceWeek(action);
  }
  if ((action.type === "explain_schedule")) {
    return await executeAiExplainSchedule(action);
  }
  if ((action.type === "delete_task")) {
    return await executeAiDeleteTask(action);
  }
  if ((action.type === "reschedule_task")) {
    return await executeAiRescheduleTask(action);
  }
  if ((action.type === "skip_occurrence")) {
    return await executeAiSkipOccurrence(action);
  }
  if ((action.type === "move_occurrence")) {
    return await executeAiMoveOccurrence(action);
  }
  if ((action.type === "resize_event")) {
    return await executeAiResizeEvent(action);
  }
  if ((action.type === "split_event")) {
    return await executeAiSplitEvent(action);
  }
  if ((action.type === "delete_all_tasks")) {
    return await executeAiDeleteAllTasks();
  }
  if ((action.type === "undo_calendar_delete")) {
    return await executeAiUndoDelete(action);
  }
  if ((action.type === "accept_all_placements")) {
    return await executeAiAcceptAllPlacements();
  }
  if ((action.type === "reoptimize_schedule")) {
    let _preSch = getStoreSchedule();
    let _preOv = getStoreOverflow();
    let _prePlacedSeen = {};
    let _prePlacedN = 0;
    for (const _pb of _preSch) {
      if (((_pb && !_pb.is_fixed) && _pb.task_id)) {
        let _pbk = String(_pb.task_id);
        if ((_prePlacedSeen[_pbk] === undefined)) {
          _prePlacedSeen[_pbk] = true;
          _prePlacedN += 1;
        }
      }
    }
    let _preOvN = (_jac.builtin.bool(_preOv) ? _preOv.length : 0);
    await runOptimize();
    let _weekIsoRo = (window.__skoochWeekIsoDays ? window.__skoochWeekIsoDays : []);
    let _todayIsoRo = _aiDateToIso(Reflect.construct(Date, []));
    let _postSch = getStoreSchedule();
    let _postOv = getStoreOverflow();
    let _postPlacedSeen = {};
    let _postPlacedN = 0;
    for (const _pb2 of _postSch) {
      if (((_pb2 && !_pb2.is_fixed) && _pb2.task_id)) {
        let _pbk2 = String(_pb2.task_id);
        if ((_postPlacedSeen[_pbk2] === undefined)) {
          _postPlacedSeen[_pbk2] = true;
          _postPlacedN += 1;
        }
      }
    }
    let _postOvN = (_jac.builtin.bool(_postOv) ? _postOv.length : 0);
    if ((((_postPlacedN === _prePlacedN) && (_postOvN === _preOvN)) && (_postPlacedN > 0))) {
      return ((("Your week's already set \u2014 all " + String(_postPlacedN)) + ((_postPlacedN === 1) ? " task is" : " tasks are")) + " on the calendar. Nothing to move.");
    }
    if (((_postPlacedN === 0) && (_postOvN === 0))) {
      return "Nothing to schedule yet \u2014 your task list is empty. Add a task and I'll fit it in.";
    }
    let _optSummary = summarizeOptimizeOutcome(_postSch, _postOv, getStoreTasks(), _weekIsoRo, _todayIsoRo);
    let _isAnonNow = (window.__skoochIsAnon !== false);
    let _profileDone = (window.__skoochProfileComplete === true);
    if ((!_isAnonNow && _profileDone)) {
      return _optSummary;
    }
    if (_isAnonNow) {
      return (("Here's a preview of your week. " + _optSummary) + " Sign up and finish the quick setup and I'll build your fully optimized schedule.");
    }
    return (("Here's a preview of your week. " + _optSummary) + " Finish the quick survey and I'll build your fully optimized schedule.");
  }
  if ((action.type === "replan")) {
    return await executeAiReplan(action);
  }
  if ((action.type === "propose_plan")) {
    let _planLabel = (_jac.poly.and_(action, () => action.plan_label) ? String(action.plan_label).trim() : "Proposed plan");
    let _items = (_jac.poly.and_(action, () => action.items) ? action.items : []);
    if ((_items.length === 0)) {
      return "I had a plan in mind, but no items were included. Can you tell me more?";
    }
    setStoreAiPendingPlan({"plan_label": _planLabel, "items": _items, "source_prompt": sourcePrompt});
    return "";
  }
  if ((action.type === "suggest_times")) {
    let _forTask = (_jac.poly.and_(action, () => action.for_task) ? String(action.for_task).trim() : "");
    let _suggestions = (_jac.poly.and_(action, () => action.suggestions) ? action.suggestions : []);
    if ((_suggestions.length === 0)) {
      return "I looked for open slots, but couldn't find any. Try adjusting your schedule constraints.";
    }
    let _mapped = [];
    for (const _sg of _suggestions) {
      _mapped.push({"start_time": (_sg.start_time ? String(_sg.start_time) : ""), "end_time": (_sg.end_time ? String(_sg.end_time) : ""), "reason": (_sg.reason ? String(_sg.reason) : ""), "for_task": _forTask, "target_date": (action.target_date ? String(action.target_date) : "")});
    }
    setStoreAiTimeSuggestions(_mapped);
    return "";
  }
  return "I understood the request, but that action type isn't wired into the app yet.";
}
async function confirmAiPlan() {
  let _pendingPlan = getStoreAiPendingPlan();
  if (((!_jac.builtin.bool(_pendingPlan) || !_pendingPlan.items) || (_pendingPlan.items.length === 0))) {
    return;
  }
  window.__skoochAiChatBusy = true;
  setStoreAiChatBusy(true);
  let _sourcePrompt = (_pendingPlan.source_prompt ? _pendingPlan.source_prompt : "");
  let _results = [];
  let _planCreatedIds = [];
  let _planAccumulatedTasks = [];
  let _weekIsoCp = (window.__skoochWeekIsoDays ? window.__skoochWeekIsoDays : []);
  let _todayIsoCp = _aiDateToIso(Reflect.construct(Date, []));
  let _validatedPlan = validateAiActions(_pendingPlan.items, _sourcePrompt, _weekIsoCp, _todayIsoCp);
  let _planItems = _validatedPlan["actions"];
  let _planIntended = buildIntendedFromActions(_planItems);
  for (const item of _planItems) {
    if ((item.type === "create_task")) {
      let _planCr = await executeAiCreateTaskResult(item, _sourcePrompt);
      if (_planCr.ok) {
        _planCreatedIds.push(_planCr.id);
        if (_planCr.task) {
          _planAccumulatedTasks.push(_planCr.task);
        }
      }
      if ((_planCr.msg && _planCr.msg.trim())) {
        _results.push(_planCr.msg.trim());
      }
    } else {
      let _summary = await executeAiAction(item, _sourcePrompt);
      if (_summary) {
        _results.push(_summary);
      }
    }
  }
  let _planFreshSchTasks = [];
  if ((_planCreatedIds.length > 0)) {
    _planFreshSchTasks = window.__skoochMergeChatCreatedTasks(_planAccumulatedTasks);
  }
  let _planOptimizeRan = false;
  if ((_planCreatedIds.length > 0)) {
    await runOptimize(_planFreshSchTasks, null, null, null);
    _planOptimizeRan = true;
  }
  if ((_planIntended.length > 0)) {
    let _planOutcome = reconcileActionOutcomes(_planIntended, getStoreSchedule(), getStoreOverflow(), _weekIsoCp, _todayIsoCp, _planOptimizeRan);
    let _planPart = partitionCreationSummaries(_results, _planOutcome);
    _results = _planPart["rest"];
    let _planOutcomeMsg = buildOutcomeMessage(_planOutcome, _planPart["byTitle"]);
    if ((_planOutcomeMsg && _planOutcomeMsg.trim())) {
      _results.push(_planOutcomeMsg.trim());
    }
  }
  let _planNotes = _validatedPlan["notes"].concat(_validatedPlan["refusals"]);
  for (const _pn of _planNotes) {
    if ((_pn && String(_pn).trim())) {
      _results.push(String(_pn).trim());
    }
  }
  let _planLabel = (_pendingPlan.plan_label ? _pendingPlan.plan_label : "Plan");
  window.__skoochPushChatMessage({"role": "assistant", "content": ((_planLabel + " confirmed! ") + groupActionSummaries(_results))});
  setStoreAiPendingPlan({});
  try {
    await window.__skoochRefreshDashboardAfterChat();
  } catch (__jac_e) {
    if (true) {
      let _cprErr = __jac_e;
      console.log("confirmAiPlan: refresh failed (non-fatal):", _cprErr);
    } else {
      throw __jac_e;
    }
  }
  window.__skoochAiChatBusy = false;
  setStoreAiChatBusy(false);
}
function rejectAiPlan() {
  setStoreAiPendingPlan({});
  window.__skoochPushChatMessage({"role": "assistant", "content": "No problem! Tell me what you'd like to change and I'll revise the plan."});
}
async function acceptTimeSuggestion(suggestion) {
  if (!_jac.builtin.bool(suggestion)) {
    return;
  }
  window.__skoochAiChatBusy = true;
  setStoreAiChatBusy(true);
  let _activeId = getStoreAiActiveChatId();
  let _msgs = getStoreAiChatMessages();
  let _forTask = (suggestion.for_task ? String(suggestion.for_task).trim() : "");
  let _startTime = (suggestion.start_time ? String(suggestion.start_time) : "");
  let _endTime = (suggestion.end_time ? String(suggestion.end_time) : "");
  let _targetDate = (suggestion.target_date ? String(suggestion.target_date) : "");
  let _durMins = minutesFromClockRange(_startTime, _endTime);
  if ((_forTask && (_durMins > 0))) {
    let _action = {"type": "create_fixed_event", "title": _forTask, "category": "other", "start_time": _startTime, "end_time": _endTime, "days": [], "is_recurring": false, "scheduled_date": _targetDate, "duration_mins": _durMins};
    let _msg = await executeAiCreateFixedEvent(_action);
    window.__skoochPushChatMessage({"role": "assistant", "content": ((((((("Locked in " + _forTask) + " at ") + _startTime) + "-") + _endTime) + ". ") + _msg)});
  } else if (_activeId) {
    saveChatToStorage(_activeId, _msgs);
  }
  setStoreAiTimeSuggestions([]);
  try {
    await window.__skoochRefreshDashboardAfterChat();
  } catch (__jac_e) {
    if (true) {
      let _atsErr = __jac_e;
      console.log("acceptTimeSuggestion: refresh failed (non-fatal):", _atsErr);
    } else {
      throw __jac_e;
    }
  }
  window.__skoochAiChatBusy = false;
  setStoreAiChatBusy(false);
}
export {_findSeriesOccurrence, _isoToWeekdayMon1, _replanFixedEvent, _rescheduleFixedEvent, _resizeFlexTask, _weekdayPlural, acceptTimeSuggestion, autoAcceptAiTask, confirmAiPlan, executeAiAcceptAllPlacements, executeAiAction, executeAiAddRule, executeAiCreateFixedEvent, executeAiCreateTask, executeAiCreateTaskResult, executeAiDeleteAllTasks, executeAiDeleteTask, executeAiExplainSchedule, executeAiMoveOccurrence, executeAiRebalanceWeek, executeAiRepeatTask, executeAiReplan, executeAiRescheduleTask, executeAiResizeEvent, executeAiSetGap, executeAiSkipOccurrence, executeAiSplitEvent, executeAiUndoDelete, rejectAiPlan};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:executeAiCreateTaskResult": {"is_writer": false, "touches": ["*"]}, "func:executeAiCreateTask": {"is_writer": false, "touches": ["*"]}, "func:executeAiCreateFixedEvent": {"is_writer": false, "touches": ["*"]}, "func:executeAiAddRule": {"is_writer": false, "touches": ["*"]}, "func:executeAiSetGap": {"is_writer": false, "touches": ["*"]}, "func:executeAiRepeatTask": {"is_writer": false, "touches": ["*"]}, "func:executeAiRebalanceWeek": {"is_writer": false, "touches": ["*"]}, "func:executeAiExplainSchedule": {"is_writer": false, "touches": ["*"]}, "func:executeAiDeleteTask": {"is_writer": false, "touches": ["*"]}, "func:executeAiUndoDelete": {"is_writer": false, "touches": ["*"]}, "func:_isoToWeekdayMon1": {"is_writer": false, "touches": ["*"]}, "func:_weekdayPlural": {"is_writer": false, "touches": ["*"]}, "func:_rescheduleFixedEvent": {"is_writer": false, "touches": ["*"]}, "func:_findSeriesOccurrence": {"is_writer": false, "touches": ["*"]}, "func:executeAiSkipOccurrence": {"is_writer": false, "touches": ["*"]}, "func:executeAiMoveOccurrence": {"is_writer": false, "touches": ["*"]}, "func:executeAiRescheduleTask": {"is_writer": false, "touches": ["*"]}, "func:_resizeFlexTask": {"is_writer": false, "touches": ["*"]}, "func:executeAiResizeEvent": {"is_writer": false, "touches": ["*"]}, "func:executeAiSplitEvent": {"is_writer": false, "touches": ["*"]}, "func:executeAiDeleteAllTasks": {"is_writer": false, "touches": ["*"]}, "func:autoAcceptAiTask": {"is_writer": false, "touches": ["*"]}, "func:executeAiAcceptAllPlacements": {"is_writer": true, "touches": ["*"]}, "func:_replanFixedEvent": {"is_writer": false, "touches": ["*"]}, "func:executeAiReplan": {"is_writer": false, "touches": ["*"]}, "func:executeAiAction": {"is_writer": true, "touches": ["*"]}, "func:confirmAiPlan": {"is_writer": true, "touches": ["*"]}, "func:rejectAiPlan": {"is_writer": false, "touches": ["*"]}, "func:acceptTimeSuggestion": {"is_writer": true, "touches": ["*"]}}); };
//# sourceMappingURL=chat_actions.js.map
